import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PrimaryButton from 'commons/src/components/buttons/PrimaryButton';
import SearchComponent from 'commons/src/components/input/SearchComponent';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import ModalWrapper from 'commons/src/components/modals/ModalWrapper';
import DeviceIcon from 'commons/src/img/deviceIcons/icon_device';
import SearchIllustration from 'commons/src/img/Illustrations/search-illustration';
import { useSearchPartnerDevicesQuery } from '../../../api/partnerHealthStatusApi';
import { Store } from '../../../reducers';
import styles from './PartnerSearchModal.module.scss';

type Props = {
    onClose: () => void;
};

const PartnerSearchModal = ({ onClose }: Props): React.ReactElement => {
    const { t: txt } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const [triggerSearch, setTriggerSearch] = useState(false);
    const selectedGroup = useSelector((store: Store) => store.userSettings.selectedGroup);
    const userGroupId = selectedGroup?.id || '';

    const {
        data: searchResults,
        isLoading,
        isFetching,
        error,
    } = useSearchPartnerDevicesQuery(
        { query: searchQuery, userGroupId },
        { skip: !triggerSearch || !searchQuery || searchQuery.length < 4 }
    );

    const isSearching = isLoading || isFetching;

    const handleSearch = (): void => {
        if (searchQuery && searchQuery.length >= 4) {
            setTriggerSearch(true);
        }
    };

    const handleSearchInputChange = (text: string): void => {
        setSearchQuery(text);
        setTriggerSearch(false);
    };

    return (
        <ModalWrapper
            isOpen
            header={txt('Partner.SearchDevices')}
            onClose={onClose}
            size="medium"
            className={styles.searchModal}
        >
            <div className={styles.searchContent}>
                <div className={styles.searchInputContainer}>
                    <SearchComponent
                        onChange={handleSearchInputChange}
                        placeholder={txt('Partner.SearchDevicesPlaceholder')}
                        size="md"
                        fullWidth
                    />
                    <div className={styles.searchButtonWrapper}>
                        <PrimaryButton
                            color="primary"
                            onClick={handleSearch}
                            title="Search"
                            icon={<MaterialIcon name="search" />}
                            disabled={!searchQuery || searchQuery.length < 4}
                            loading={isSearching}
                            testId="partner-search-submit-button"
                        />
                    </div>
                </div>

                {error && <div className={styles.error}>{txt('ErrorCodes.BAD_REQUEST')}</div>}
                {triggerSearch && !isSearching && !error && searchResults && searchResults.length === 0 && (
                    <figure className={styles.noResults}>
                        <SearchIllustration />
                        <figcaption>{txt('BuildingHealth.SearchNoResults')}</figcaption>
                    </figure>
                )}
                {triggerSearch && !isSearching && searchResults && searchResults.length > 0 && (
                    <div className={styles.results}>
                        <div className={styles.resultsHeader}>
                            {txt('Partner.SearchResultsCount', { count: searchResults.length })}
                        </div>
                        <div className={styles.resultsList}>
                            {searchResults.map(result => (
                                <div key={`${result.serialNumber}-${result.userGroupId}`} className={styles.resultItem}>
                                    <div className={styles.resultMain}>
                                        <div className={styles.deviceInfo}>
                                            <div className={styles.deviceIconWrapper}>
                                                <DeviceIcon />
                                            </div>
                                            <div>
                                                <div className={styles.deviceName}>{result.segmentName}</div>
                                                <div className={styles.serialNumber}>{result.serialNumber}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.resultMeta}>
                                        <div className={styles.customer}>
                                            <MaterialIcon name="people_outline" extraClass={styles.metaIcon} />
                                            {result.userGroupName}
                                        </div>
                                        <div className={styles.location}>
                                            <MaterialIcon name="business" extraClass={styles.metaIcon} />
                                            {result.locationName}
                                        </div>
                                        {result.spaceName && (
                                            <div className={styles.space}>
                                                <MaterialIcon name="meeting_room" extraClass={styles.metaIcon} />
                                                {result.spaceName}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </ModalWrapper>
    );
};

export default PartnerSearchModal;
